/** Oyunun kendi sayfası (`/oyun/:id`) — adres ve mod sözleşmesi.
 *
 * Şema `gamePage` taşıyorsa (bugün yalnızca Betrun) oyun pencerede değil bu
 * sayfada açılır: solda oyun, altında araç şeridi, sağda öneri sütunu. Oyun
 * bir rota olduğu için adres paylaşılabilir, sayfa yenilenince oyun yeniden
 * açılır ve cihazın geri hareketi oyuncuyu geldiği sayfaya götürür.
 *
 * Adres Türkçedir ve bir API'dir: `?mod=demo` demo oyunudur, parametresiz
 * adres gerçek paradır. Gerçek oyun **varsayılandır** — paylaşılan bir
 * bağlantının oyuncuyu sessizce demo bakiyesine düşürmesi, kazancının
 * yazılmadığı bir oyunda oynatmak olurdu.
 */

export const GAME_PAGE_BASE = '/oyun';
export const GAME_MODE_PARAM = 'mod';
export const MODE_REAL = 'real';
export const MODE_DEMO = 'demo';

/** Oyun sayfasının adresi. */
export const gamePagePath = (gameId, mode = MODE_REAL) => {
  const id = encodeURIComponent(String(gameId || ''));
  const query = mode === MODE_DEMO ? `?${GAME_MODE_PARAM}=demo` : '';
  return `${GAME_PAGE_BASE}/${id}${query}`;
};

/** Adresteki mod; tanınmayan değer gerçek oyundur. */
export const gameModeFromSearch = (search) => {
  const params = new URLSearchParams(String(search || ''));
  return params.get(GAME_MODE_PARAM) === 'demo' ? MODE_DEMO : MODE_REAL;
};

/** Oyunun demosu sunulabilir mi.
 *
 * `useGameLauncher.demoAvailable` ile aynı kuraldır: `demo_supported` sunucudan
 * efektif değer olarak gelir, canlı masa ise sağlayıcı ne derse desin demo
 * vermez (bkz. CLAUDE.md → *Reel/demo eşleştirmesi*).
 */
export const gameDemoAvailable = (game) => (
  !!game?.id
  && game?.demo_supported === true
  && !game?.live
  && game?.category !== 'live-casino'
);

/** Volatilite (oynaklık) — katalogdan gelen anahtarın etiketi ve seviyesi.
 *
 * Anahtar kümesi sunucudakiyle aynıdır (`backend/services/game_volatility.py`
 * → `VOLATILITY_LEVELS`). Değeri yalnızca bildiren sağlayıcılar yazar; boş ya
 * da tanınmayan değerde `null` döner ve sayfa satırı hiç çizmez — bilinmeyen
 * oynaklık uydurulmaz.
 */
export const VOLATILITY_LABELS = Object.freeze({
  low: { label: 'Düşük', level: 1 },
  'low-medium': { label: 'Düşük-Orta', level: 2 },
  medium: { label: 'Orta', level: 3 },
  'medium-high': { label: 'Orta-Yüksek', level: 4 },
  high: { label: 'Yüksek', level: 5 },
  'very-high': { label: 'Çok Yüksek', level: 5 },
});
export const VOLATILITY_MAX_LEVEL = 5;

export const gameVolatility = (game) => {
  const key = String(game?.volatility || '').trim().toLowerCase();
  const info = VOLATILITY_LABELS[key];
  return info ? { key, ...info } : null;
};

/** Sağ sütunun önerileri: açık oyun çıkarılır, tekrarlar atılır. */
export const pickRecommendations = (items, currentId, limit = 12) => {
  const seen = new Set([String(currentId || '')]);
  const picked = [];
  (Array.isArray(items) ? items : []).forEach((game) => {
    const id = String(game?.id || '');
    if (!id || seen.has(id) || picked.length >= limit) return;
    seen.add(id);
    picked.push(game);
  });
  return picked;
};

// "Son Kazançlar" tablosu: oyunun kendi turlarından, sunucuda maskelenmiş
// adlarla gelir (`GET /games/item/:id/wins`).
export const GAME_WINS_LIMIT = 10;

export const gameWinsPath = (gameId) => `/games/item/${encodeURIComponent(gameId)}/wins`;

// Tablonun eşiği panel ayarıdır ve yanıtta `min_multiplier` olarak gelir;
// gelmezse (eski sunucu) varsayılan yazılır. "10x", "7,5x".
export const DEFAULT_WIN_THRESHOLD = 10;

export const formatWinThreshold = (value) => {
  const number = Number(value);
  const safe = Number.isFinite(number) && number > 0 ? number : DEFAULT_WIN_THRESHOLD;
  return `${safe.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}x`;
};

const WIN_TIME_ZONE = 'Europe/Istanbul';

// Parçalar elle birleştirilir: `toLocale*String` ayraçları tarayıcının ICU
// verisine bağlıdır (topMatches.formatMatchTime ile aynı gerekçe).
const istanbulParts = (date) => {
  const parts = {};
  new Intl.DateTimeFormat('en-GB', {
    timeZone: WIN_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(date).forEach(({ type, value }) => { parts[type] = value; });
  return parts;
};

/**
 * Turun zamanı — Türkiye saatiyle. Bugünse yalnızca saat ("20:08"), değilse
 * gün de yazılır ("26.09 20:08"). Çözülemeyen zaman boş kalır.
 */
export const formatWinTime = (value, now = new Date()) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = istanbulParts(date);
  const today = istanbulParts(now);
  const time = `${parts.hour}:${parts.minute}`;
  const sameDay = parts.year === today.year && parts.month === today.month
    && parts.day === today.day;
  return sameDay ? time : `${parts.day}.${parts.month} ${time}`;
};
